'use client';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { Button, ImageAnimated, Typography } from 'components';
import Wizard from 'components/Wizard';

import { MantineProvider } from '@mantine/core';
import Accordions from 'components/Accordions';
import CardsGrid from 'components/CardsGrid';
import CardsGridBanner from 'components/CardsGridBanner';
import Entering from 'components/Entering';
import { LazyLottie } from 'components/LazyLottie';
import { CLIENTS_SING_UP_LINK } from 'constants/links';
import { MIN_WIDTH_LG } from 'constants/media';
import {
  AiTextClipArt,
  ExpertServicesClipArt,
  OverallProgressClipArt,
  StyleGlossaryClipArt
} from 'lib/ClipArts';
import {
  AiDrivenIcon,
  AiIcon,
  BulkIcon,
  ChipIcon,
  ControlIcon,
  CostIcon,
  CreativeIcon,
  ExpertsIcon,
  IntegrateIcon,
  InteligentIcon,
  InvestmentIcon,
  LangsIcon,
  LocalizingIcon,
  PathIcon,
  PrecisionIcon,
  PuzzleIcon,
  PuzzleUnmountedIcon,
  RescueIcon,
  ScaleIcon,
  SpeedIcon,
  UnwaveringIcon,
  WorldIcon
} from 'lib/Icons';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { useMediaPredicate } from 'react-media-hook';
import { cn } from 'utils/cn';

export const dynamic = 'force-dynamic';

const PageHome = () => {
  const t = useTranslations();

  const isLg = useMediaPredicate(MIN_WIDTH_LG);

  const classNameSvgs =
    'absolute mx-auto my-auto !max-h-[50dvh] !max-w-[50dvh] lg:!h-[450px] lg:px-0';

  const classNameImages = cn(classNameSvgs, '!left-auto !right-auto object-contain !h-[450px]');

  return (
    <>
      {/* AI-Driven Localization Unleashed */}
      <div className="scroll-smooth">
        <section className="flex h-[calc(100dvh_-_71px)] lg:h-[calc(100dvh_-_120px)]">
          <div className="inline-flex size-full flex-col items-center justify-start overflow-hidden bg-white">
            <div className="block size-full max-w-6xl lg:px-16">
              <div
                className={cn(
                  'flex h-[calc(100dvh_-_71px)] flex-col-reverse p-6 sm:p-16',
                  'lg:h-[calc(100dvh_-_120px)] lg:flex-row lg:p-0'
                )}
              >
                <div className="relative flex h-1/2 min-h-[320px] flex-1 flex-col justify-between sm:py-0 lg:h-full lg:w-1/2 lg:py-10">
                  <Entering
                    delay={isLg ? 0 : 0.2}
                    className="flex w-full flex-1 flex-col lg:w-fit"
                    position="left"
                  >
                    <Typography variant="bold16xl" className="!m-0 text-tolq-black-100">
                      {t('home.block_one.heading')}
                    </Typography>
                    <div className="my-auto sm:my-4">
                      <Typography
                        variant="light1"
                        className="!my-auto !text-[14px] text-tolq-black-200 sm:!text-3xl"
                      >
                        <span className="sm:hidden">{t('home.block_one.description_mobile')}</span>
                        <span className="hidden sm:block">{t('home.block_one.description')}</span>
                      </Typography>
                    </div>
                  </Entering>
                  <div className="flex flex-col items-center gap-4 sm:w-3/5 sm:flex-row lg:w-fit">
                    <Entering
                      damping={18}
                      stiffness={120}
                      className="flex w-full flex-1 flex-col lg:w-fit"
                      delay={0.4}
                    >
                      <Link href="/book-now" className="w-full lg:w-fit">
                        <Button
                          size="medium"
                          variant="contained"
                          className="w-full !py-6"
                          color="secondary"
                          label={t('home.block_one.buttons.book_a_demo')}
                        />
                      </Link>
                    </Entering>
                    <Entering
                      damping={18}
                      stiffness={120}
                      delay={0.6}
                      className="flex w-full flex-1 flex-col lg:w-fit"
                    >
                      <Link href={CLIENTS_SING_UP_LINK} className="w-full lg:w-fit">
                        <Button
                          size="medium"
                          variant="outlined"
                          color="secondary"
                          label={t('home.block_one.buttons.start_free')}
                          className="w-full !py-6"
                        />
                      </Link>
                    </Entering>
                  </div>
                </div>
                <div
                  className={cn(
                    'relative mb-16 flex min-h-[30dvh] items-center justify-center xm:mb-6',
                    'sm:min-h-[43dvh] lg:mb-0 lg:h-full lg:min-h-full lg:w-1/2'
                  )}
                >
                  <span className="absolute scale-[1.30] xm:w-[60dvw] xm:scale-100 sm:scale-75 lg:left-10 lg:w-auto lg:scale-125">
                    <MantineProvider>
                      <LazyLottie
                        id="empty-box"
                        getAnimationData={() => import('../../../../public/animations/hola.json')}
                        width={'30dvh'}
                        height={'30dvh'}
                      />
                    </MantineProvider>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* Why Global Industry Leaders Choose Tolq? */}
      <div className="scroll-smooth">
        <section className="snap-start snap-always lg:h-[calc(100dvh_-_76px)]">
          <div className="inline-flex size-full flex-col items-center justify-start bg-tolq-gray-200 ">
            <div className="block size-full max-w-6xl p-6 pb-20 sm:p-0 sm:px-16">
              <div className="flex h-full flex-col-reverse lg:flex-row">
                <div
                  className={cn(
                    'relative flex h-[60vw] justify-center sm:my-10 sm:w-full',
                    'lg:m-0 lg:h-full lg:w-1/2 lg:items-center '
                  )}
                >
                  <ImageAnimated
                    initial={false}
                    position="left"
                    src="/images/cliparts/png/project_progress.png"
                    alt="Project Progress"
                    className="absolute mx-0 my-auto h-full object-contain"
                  />
                </div>
                <div
                  className={cn(
                    'relative flex h-1/2 flex-1 flex-col justify-center pt-10 sm:h-full sm:w-full',
                    'lg:w-1/2 lg:pl-8'
                  )}
                >
                  <div className="relative h-[600px] lg:h-[480px]">
                    <Entering initial={false} className="mb-8">
                      <Typography
                        variant="bold8xl"
                        className={cn(
                          '!m-0 !mb-4 flex w-full justify-center !text-6xl !leading-8 text-tolq-black-100',
                          'sm:!text-[40px] sm:!leading-[40px]'
                        )}
                      >
                        <div className="max-w-[250px] text-center sm:max-w-full">
                          {t('home.block_two.heading')}
                        </div>
                      </Typography>
                    </Entering>
                    <div className="absolute w-full">
                      <Accordions
                        acordions={[
                          {
                            title: (
                              <div className="flex min-h-12 items-center gap-4">
                                <WorldIcon />
                                <Typography variant="bold2" className="text-tolq-black-100">
                                  {t('home.block_two.accordeon.0.heading')}
                                </Typography>
                              </div>
                            ),
                            children: (
                              <Typography variant="light3" className="text-tolq-black-200">
                                {t('home.block_two.accordeon.0.description')}
                              </Typography>
                            )
                          },
                          {
                            title: (
                              <div className="flex min-h-12 items-center gap-4">
                                <PuzzleIcon />
                                <Typography variant="bold2" className="text-tolq-black-100">
                                  {t('home.block_two.accordeon.1.heading')}
                                </Typography>
                              </div>
                            ),
                            children: (
                              <Typography variant="light3" className="text-tolq-black-200">
                                {t('home.block_two.accordeon.1.description')}
                              </Typography>
                            )
                          },
                          {
                            title: (
                              <div className="flex min-h-12 items-center gap-4">
                                <RescueIcon />
                                <Typography variant="bold2" className="text-tolq-black-100">
                                  {t('home.block_two.accordeon.2.heading')}
                                </Typography>
                              </div>
                            ),
                            children: (
                              <Typography variant="light3" className="text-tolq-black-200">
                                {t('home.block_two.accordeon.2.description')}
                              </Typography>
                            )
                          }
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* Elevating Global Engagement?  */}
      <div className="scroll-smooth">
        <section className="">
          <div className="inline-flex size-full flex-col items-center justify-start overflow-hidden bg-white">
            <div className="block size-full max-w-6xl p-6 pt-16  sm:p-16">
              <Entering
                initial={false}
                amount={'all'}
                duration={4}
                className="flex h-full flex-col"
              >
                <div className="mb-6 flex w-full flex-col justify-center">
                  <Typography
                    variant="medium3"
                    className="!m-0 !mb-3 w-full text-center text-tolq-green-100"
                  >
                    {t('home.block_three.sub_heading')}
                  </Typography>
                  <Typography
                    variant="bold8xl"
                    className="!m-0 !mb-4 w-full text-center !text-6xl !leading-8 text-tolq-black-100 lg:!text-[40px] lg:!leading-[40px]"
                  >
                    {t('home.block_three.heading')}
                  </Typography>
                  <Typography variant="light1" className="!m-0 text-center text-tolq-black-200">
                    {t('home.block_three.description')}
                  </Typography>
                  <Typography
                    variant="light1"
                    className="!m-0 !mb-3 text-center text-tolq-black-200"
                  >
                    {t('home.block_three.sub_description')}
                  </Typography>
                </div>
              </Entering>
              <div className="mb-16 flex flex-col gap-20 lg:my-20 lg:mb-0 lg:gap-52">
                {[
                  {
                    heading: (
                      <div className="mb-6">
                        <Typography variant="bold8xl" className="!m-0 !mb-6 text-tolq-black-100">
                          {t('home.block_three.context.0.heading.0')}
                        </Typography>
                        &nbsp; &nbsp;
                        <Typography variant="bold8xl" className="!m-0 !mb-6 text-tolq-green-100">
                          {t('home.block_three.context.0.heading.1')}
                        </Typography>
                      </div>
                    ),
                    description: t('home.block_three.context.0.description'),
                    clipArt: (
                      <ImageAnimated
                        initial={false}
                        position="right"
                        src="/images/cliparts/png/comments.png"
                        alt="Comments"
                        className={classNameImages}
                        style={isLg ? { scale: 1.3 } : {}}
                      />
                    )
                  },
                  {
                    heading: (
                      <div className="mb-6">
                        <Typography variant="bold8xl" className="!m-0 !mb-6 text-tolq-green-100">
                          {t('home.block_three.context.1.heading.0')}
                        </Typography>
                        &nbsp; &nbsp;
                        <Typography variant="bold8xl" className="!m-0 !mb-6 text-tolq-black-100">
                          {t('home.block_three.context.1.heading.1')}
                        </Typography>
                      </div>
                    ),
                    description: t('home.block_three.context.1.description'),
                    clipArt: (
                      <ImageAnimated
                        initial={false}
                        position="left"
                        src="/images/cliparts/png/accuracy.png"
                        alt="Accuracy"
                        className={classNameImages}
                        style={isLg ? { scale: 1.3 } : {}}
                      />
                    )
                  },
                  {
                    heading: (
                      <div className="mb-6">
                        <Typography variant="bold8xl" className="!m-0 !mb-6 text-tolq-black-100">
                          {t('home.block_three.context.2.heading.0')}
                        </Typography>
                        &nbsp; &nbsp;
                        <Typography variant="bold8xl" className="!m-0 !mb-6 text-tolq-green-100">
                          {t('home.block_three.context.2.heading.1')}
                        </Typography>
                        &nbsp; &nbsp;
                        <Typography variant="bold8xl" className="!m-0 !mb-6 text-tolq-black-100">
                          {t('home.block_three.context.2.heading.2')}
                        </Typography>
                      </div>
                    ),
                    description: t('home.block_three.context.2.description'),
                    clipArt: (
                      <ImageAnimated
                        initial={false}
                        position="right"
                        src="/images/cliparts/png/style_guide.png"
                        alt="Style Guide"
                        className={classNameImages}
                        style={isLg ? { scale: 1.3 } : {}}
                      />
                    )
                  }
                ].map(({ heading, description, clipArt }, index) => (
                  <div
                    key={description}
                    className={cn(
                      'flex flex-col justify-between gap-20 lg:flex-row',
                      index % 2 !== 0 && 'lg:flex-row-reverse'
                    )}
                  >
                    <div className="relative flex flex-1 flex-col justify-start py-10 sm:py-0 lg:w-1/2">
                      <Entering initial={false} amount={0.9} className="mb-6">
                        {heading}
                        <Typography variant="light1" className="text-tolq-black-200">
                          <span
                            dangerouslySetInnerHTML={{
                              __html: description
                            }}
                          />
                        </Typography>
                      </Entering>
                      <Entering
                        initial={false}
                        amount={0.9}
                        damping={18}
                        stiffness={120}
                        delay={0.2}
                      >
                        <Link href="/solutions">
                          <Button
                            size="large"
                            variant="text"
                            color="secondary"
                            label={t('home.block_three.context.0.learn_more')}
                            className="!p-0"
                            classNameTypography="!font-normal"
                            endAdornment={<ChevronRightIcon className="w-6 !text-tolq-blue-100" />}
                          />
                        </Link>
                      </Entering>
                    </div>
                    <div className="relative my-24 flex items-center justify-center lg:my-0 lg:w-5/12">
                      {clipArt}
                    </div>
                  </div>
                ))}
                <Entering
                  initial={false}
                  amount={0.9}
                  damping={18}
                  stiffness={120}
                  delay={0.2}
                  className="flex w-full items-center justify-center pt-10 lg:pt-0"
                >
                  <Link href="/solutions">
                    <Button
                      size="large"
                      variant="contained"
                      className="!py-6"
                      color="secondary"
                      label={t('home.block_five.learn_more')}
                    />
                  </Link>
                </Entering>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* Features That Set Us Apart */}
      <div className="scroll-smooth">
        <section className="h-[calc(100dvh_-_72px)] ">
          <div className="inline-flex size-full flex-col items-center justify-start overflow-hidden bg-tolq-gray-200">
            <div className="block size-full max-w-6xl px-6 sm:px-16 lg:px-16">
              <div className="flex h-full flex-col justify-evenly">
                <Entering
                  initial={false}
                  amount={'all'}
                  delay={0.4}
                  duration={4}
                  className="flex w-full flex-col justify-center"
                >
                  <Typography
                    variant="medium3"
                    className="!m-0 !mb-3 w-full text-center text-tolq-green-100"
                  >
                    {t('home.block_four.sub_heading')}
                  </Typography>
                  <Typography
                    variant="bold8xl"
                    className="!m-0 !mb-4 w-full  !text-6xl !leading-8 text-tolq-black-100 lg:!text-[40px] lg:!leading-[40px]"
                  >
                    <div className="text-center">{t('home.block_four.heading')}</div>
                  </Typography>
                </Entering>
                <Entering initial={false} amount={'all'}>
                  <Wizard
                    tabs={[
                      {
                        label: t('home.block_four.context.0.heading'),
                        content: t('home.block_four.context.0.description'),
                        clipart: (
                          <Entering
                            initial={false}
                            amount={0.7}
                            damping={12}
                            stiffness={120}
                            duration={0.4}
                          >
                            <AiTextClipArt className="absolute mx-0 my-auto !h-[450px]" />
                          </Entering>
                        ),
                        icon: <AiIcon />
                      },
                      {
                        label: t('home.block_four.context.1.heading'),
                        content: t('home.block_four.context.1.description'),
                        clipart: (
                          <Entering
                            initial={false}
                            amount={0.7}
                            damping={12}
                            stiffness={120}
                            duration={0.4}
                          >
                            <OverallProgressClipArt className="absolute mx-0 my-auto !h-[450px]" />
                          </Entering>
                        ),
                        icon: <LangsIcon />
                      },
                      {
                        label: t('home.block_four.context.2.heading'),
                        content: t('home.block_four.context.2.description'),
                        clipart: (
                          <Entering
                            initial={false}
                            amount={0.7}
                            damping={12}
                            stiffness={120}
                            duration={0.4}
                          >
                            <ExpertServicesClipArt className="absolute mx-0 my-auto !h-[450px]" />
                          </Entering>
                        ),
                        icon: <ExpertsIcon />
                      },
                      {
                        label: t('home.block_four.context.3.heading'),
                        content: t('home.block_four.context.3.description'),
                        clipart: (
                          <Entering
                            initial={false}
                            amount={0.7}
                            damping={12}
                            stiffness={120}
                            duration={0.4}
                          >
                            <StyleGlossaryClipArt className="absolute mx-0 my-auto !h-[450px]" />
                          </Entering>
                        ),
                        icon: <LocalizingIcon />
                      },
                      {
                        label: t('home.block_four.context.4.heading'),
                        content: t('home.block_four.context.4.description'),
                        clipart: (
                          <ImageAnimated
                            initial={false}
                            amount={'all'}
                            src="/images/cliparts/png/progress_show.png"
                            alt="Project Show"
                            className="absolute !-bottom-10 !top-0 mx-0 my-auto !h-[450px] scale-125 object-contain"
                          />
                        ),
                        icon: <CreativeIcon />
                      }
                    ]}
                  />
                </Entering>
                <Entering
                  initial={false}
                  amount={0.9}
                  damping={18}
                  stiffness={120}
                  delay={0.2}
                  className="flex w-full items-center justify-center"
                >
                  <Link href="/products">
                    <Button
                      size="medium"
                      variant="contained"
                      className="!py-6"
                      color="secondary"
                      label={t('home.block_four.explore_products')}
                    />
                  </Link>
                </Entering>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* Why Our Products Lead the Way in Localization */}
      {false && (
        <div className="scroll-smooth">
          <section className="h-[calc(100dvh_-_72px)] ">
            <div className="inline-flex size-full flex-col items-center justify-start overflow-hidden bg-white">
              <div className="block size-full max-w-6xl p-6 sm:p-16 lg:p-0 lg:px-16">
                <div className="flex h-full flex-col">
                  <Entering
                    initial={false}
                    amount={'all'}
                    delay={0.4}
                    duration={4}
                    className="flex w-full flex-col justify-center pb-[80px] pt-[120px]"
                  >
                    <Typography
                      variant="medium3"
                      className="!m-0 !mb-3 w-full text-center text-tolq-green-100"
                    >
                      {t('home.block_five.sub_heading')}
                    </Typography>
                    <Typography
                      variant="bold8xl"
                      className="!m-0 !mb-4 w-full text-center text-tolq-black-100"
                    >
                      {t('home.block_five.heading')}
                    </Typography>
                  </Entering>
                  <CardsGrid
                    cards={[
                      {
                        icon: <AiDrivenIcon className="size-14" />,
                        title: t('home.block_five.context.0.heading'),
                        description: t('home.block_five.context.0.description')
                      },
                      {
                        icon: <IntegrateIcon className="size-14" />,
                        title: t('home.block_five.context.1.heading'),
                        description: t('home.block_five.context.1.description')
                      },
                      {
                        icon: <InvestmentIcon className="size-14" />,
                        title: t('home.block_five.context.2.heading'),
                        description: t('home.block_five.context.2.description')
                      },
                      {
                        icon: <SpeedIcon className="size-14" />,
                        title: t('home.block_five.context.3.heading'),
                        description: t('home.block_five.context.3.description')
                      },
                      {
                        icon: <ControlIcon className="size-14" />,
                        title: t('home.block_five.context.4.heading'),
                        description: t('home.block_five.context.4.description')
                      },
                      {
                        icon: <BulkIcon className="size-14" />,
                        title: t('home.block_five.context.5.heading'),
                        description: t('home.block_five.context.5.description')
                      },
                      {
                        icon: <UnwaveringIcon className="size-14" />,
                        title: t('home.block_five.context.6.heading'),
                        description: t('home.block_five.context.6.description')
                      },
                      {
                        icon: <PrecisionIcon className="size-14" />,
                        title: t('home.block_five.context.7.heading'),
                        description: t('home.block_five.context.7.description')
                      },
                      {
                        icon: <InteligentIcon className="size-14" />,
                        title: t('home.block_five.context.8.heading'),
                        description: t('home.block_five.context.8.description')
                      }
                    ]}
                  />
                  <div className="flex w-full items-center justify-center pb-20 pt-10 sm:pt-0">
                    <Link href="/products">
                      <Button
                        size="large"
                        variant="contained"
                        className="!py-6"
                        color="secondary"
                        label={t('home.block_five.learn_more')}
                      />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
      {/* Optimize Your Localization Strategy */}
      <div className="scroll-smooth">
        <section className="snap-center snap-always lg:h-[calc(80dvh_-_124px)]">
          <div className="inline-flex size-full flex-col items-center justify-start overflow-hidden bg-white">
            <div className="block size-full max-w-6xl p-6 sm:p-16 lg:p-0 lg:px-16">
              <div className="flex h-full flex-col lg:flex-row">
                <Entering
                  initial={false}
                  position="left"
                  delay={0.2}
                  amount={0.3}
                  className={cn(
                    'relative flex h-1/2 flex-1 flex-col justify-center gap-6 py-10 pb-0 sm:p-16',
                    'lg:h-full lg:w-1/2 lg:py-0 lg:pl-8'
                  )}
                >
                  <div>
                    <Typography
                      variant="bold8xl"
                      className="!m-0 !mb-4 w-full  !text-6xl !leading-8 text-tolq-black-100 lg:!text-[40px] lg:!leading-[40px]"
                    >
                      <div className="text-center lg:text-left">{t('home.block_six.heading')}</div>
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="light1" className="text-tolq-black-200">
                      <div className="text-center lg:text-left">
                        {t('home.block_six.description')}
                      </div>
                    </Typography>
                  </div>
                </Entering>
                <div
                  className={cn(
                    'relative flex h-1/2 flex-1 flex-col justify-center py-10 sm:py-0 lg:h-full lg:w-1/2',
                    'lg:pl-8'
                  )}
                >
                  <div className="flex flex-col gap-6 lg:gap-6">
                    <Entering
                      initial={false}
                      delay={0.2}
                      className="rounded-6xl border-1 border-tolq-gray-100 bg-white px-6 py-4"
                    >
                      <div className="flex min-h-12 items-center gap-4 pb-4">
                        <CostIcon />
                        <Typography variant="bold2" className="text-tolq-black-100">
                          {t('home.block_six.context.0.heading')}
                        </Typography>
                      </div>
                      <div className="pl-14">
                        <Typography variant="light3" className="text-tolq-black-200">
                          {t('home.block_six.context.0.description')}
                        </Typography>
                      </div>
                    </Entering>
                    <Entering
                      initial={false}
                      delay={0.2}
                      className="rounded-6xl border-1 border-tolq-gray-100 bg-white px-6 py-4"
                    >
                      <div className="flex min-h-12 items-center gap-4 pb-4">
                        <ScaleIcon />
                        <Typography variant="bold2" className="text-tolq-black-100">
                          {t('home.block_six.context.1.heading')}
                        </Typography>
                      </div>
                      <div className="pl-14">
                        <Typography variant="light3" className="text-tolq-black-200">
                          {t('home.block_six.context.1.description')}
                        </Typography>
                      </div>
                    </Entering>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* Global Localization Reimagined */}
      <div className="scroll-smooth">
        <section className=" lg:h-[calc(100dvh_-_72px)]">
          <div className="inline-flex size-full flex-col items-center justify-start overflow-hidden bg-tolq-gray-200">
            <div className="block size-full max-w-6xl p-6 sm:p-16 lg:p-0 lg:px-16">
              <div className="flex h-full flex-col">
                <div className="h-full">
                  <Entering
                    initial={false}
                    amount={'all'}
                    duration={4}
                    className="flex w-full flex-col justify-center py-10 pb-8 pt-16 sm:pt-0 lg:pt-16"
                  >
                    <Typography
                      variant="bold8xl"
                      className="!m-0 !mb-4 w-full  !text-6xl !leading-8 text-tolq-black-100 lg:!text-[40px] lg:!leading-[40px]"
                    >
                      <div className="text-center">{t('home.block_seven.heading')}</div>
                    </Typography>
                    <Typography variant="light1" className="!m-0 text-center text-tolq-black-200">
                      {t('home.block_seven.description')}
                    </Typography>
                  </Entering>
                  <div className="absolute size-1 opacity-0">
                    <PathIcon />
                    <ChipIcon />
                    <PuzzleUnmountedIcon />
                  </div>
                  <CardsGridBanner
                    key="banner"
                    cards={[
                      {
                        icon: <ChipIcon />,
                        title: t('home.block_seven.context.0.heading')
                      },
                      {
                        icon: <PathIcon />,
                        title: t('home.block_seven.context.1.heading')
                      },
                      {
                        icon: <PuzzleUnmountedIcon />,
                        title: t('home.block_seven.context.2.heading')
                      }
                    ]}
                  />
                </div>
                <div className="flex flex-col pb-10 lg:flex-row">
                  <Entering
                    initial={false}
                    delay={0.4}
                    className={cn(
                      'relative flex h-1/2 flex-1 flex-col justify-start gap-6 py-10 sm:py-16 lg:h-full lg:w-1/2'
                    )}
                  >
                    <div>
                      <Typography variant="bold6xl" className="!m-0 !mb-6 text-tolq-black-100">
                        <div className="text-center lg:text-left">
                          {t('home.block_seven.sub_heading')}
                        </div>
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="light1" className="text-tolq-black-200">
                        <div className="text-center lg:text-left">
                          {t('home.block_seven.sub_description')}
                        </div>
                      </Typography>
                    </div>
                  </Entering>
                  <Entering
                    initial={false}
                    position={'right'}
                    delay={0.4}
                    className="flex flex-1 flex-col items-end justify-center"
                  >
                    <Link href="/book-now" className="w-full lg:w-auto">
                      <Button
                        size="medium"
                        variant="contained"
                        className="w-full !py-6"
                        color="secondary"
                        label={t('home.block_seven.book_your')}
                      />
                    </Link>
                  </Entering>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* Slider  */}
      {false && (
        <div
          className={cn(
            'inline-flex w-full flex-col items-center justify-start overflow-hidden bg-white',
            'h-[calc(100dvh_-_72px)]'
          )}
        >
          <div className="block size-full max-w-6xl p-6 sm:p-16 lg:p-0 lg:px-16">
            <div className="flex h-full flex-col items-center justify-center">
              <Image
                unoptimized
                quality={100}
                priority
                src="/images/pics/brands/olymp-no-background.png"
                height={65}
                width={130}
                alt="olymp"
                className="mb-16 object-contain"
              />
              <div className="mb-5">
                <Typography variant="regular6xl" className="!m-0 !mb-6 italic text-tolq-black-100">
                  &quot;{t('home.block_eight.context.0.review')}&ldquo;
                </Typography>
              </div>
              <Image
                unoptimized
                quality={100}
                priority
                src="/images/pics/clients/candice_wu.png"
                height={65}
                width={65}
                alt="candice_wu"
                className="mb-5 object-contain"
              />
              <div>
                <Typography variant="medium2" className="!m-0 !mb-6 text-tolq-black-100">
                  {t('home.block_eight.context.0.client_name')}
                </Typography>
              </div>
              <div className="mb-5">
                <Typography variant="light3" className="!m-0 !mb-6 text-tolq-black-200">
                  {t('home.block_eight.context.0.role')}
                  {', '}
                  {t('home.block_eight.context.0.company')}
                </Typography>
              </div>
              <div className="flex gap-8">
                <span className="size-[10px] rounded-full bg-tertiary-100 " />
                <span className="size-[10px] rounded-full bg-tolq-gray-100 " />
                <span className="size-[10px] rounded-full bg-tolq-gray-100 " />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="scroll-smooth">
        <section className="snap-center snap-always lg:h-[calc(60dvh_-_124px)]">
          <div className="inline-flex size-full flex-col items-center justify-start overflow-hidden bg-white">
            <div className="block size-full max-w-6xl p-6 sm:p-16 lg:p-0 lg:px-16">
              <div className="flex h-full flex-col justify-center">
                {/* Brands  */}
                <div className="grid grid-cols-2 gap-8 sm:self-center lg:grid-cols-6">
                  <ImageAnimated
                    initial={false}
                    damping={20}
                    mass={0.5}
                    delay={0}
                    className="h-20 w-40 object-contain"
                    src="/images/pics/brands/media_markt.png"
                    alt="Media Markt"
                  />
                  <ImageAnimated
                    initial={false}
                    damping={20}
                    mass={0.5}
                    delay={0.2}
                    className="h-20 w-40 object-contain"
                    src="/images/pics/brands/olymp.png"
                    alt="Olymp"
                  />
                  <ImageAnimated
                    initial={false}
                    damping={20}
                    mass={0.5}
                    delay={0.4}
                    className="h-20 w-40 object-contain"
                    src="/images/pics/brands/bol.png"
                    alt="bol"
                  />
                  <ImageAnimated
                    initial={false}
                    damping={20}
                    mass={0.5}
                    delay={0.6}
                    className="h-20 w-40 object-contain"
                    src="/images/pics/brands/ochsner_sport.png"
                    alt="Ochsner Sport"
                  />
                  <ImageAnimated
                    initial={false}
                    damping={20}
                    mass={0.5}
                    delay={0.8}
                    className="h-20 w-40 object-contain"
                    src="/images/pics/brands/duravit.png"
                    alt="Duravit"
                  />
                  <ImageAnimated
                    initial={false}
                    damping={20}
                    mass={0.5}
                    delay={1}
                    className="h-20 w-40 object-contain"
                    src="/images/pics/brands/boldking.png"
                    alt="Boldking"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <div className="scroll-smooth">
        <div className="inline-flex size-full flex-col items-center justify-start overflow-hidden bg-white">
          <div className="block size-full">
            <div className="flex h-full flex-col justify-between">
              {/* Elevate Your Global Impact  */}
              <div className="w-full bg-tolq-blue-000">
                <div
                  className={cn(
                    'inline-flex w-full flex-col items-center justify-start overflow-hidden bg-white',
                    'h-[409px]',
                    '!bg-transparent bg-cover !bg-no-repeat'
                  )}
                  style={{
                    backgroundImage: 'url(/images/pics/electric.png)'
                  }}
                >
                  <div className="flex size-full max-w-6xl flex-1 flex-col justify-center p-6 sm:p-16 lg:p-0 lg:px-16">
                    <div
                      className={cn(
                        'flex flex-1 flex-col items-center justify-center gap-4',
                        'lg:gap-16'
                      )}
                    >
                      <Entering initial={false} amount={'all'} duration={4} className="flex">
                        <Typography
                          variant="bold12xl"
                          className="!m-0 !mb-6 text-center text-white"
                        >
                          {t('home.block_ten.heading')}
                        </Typography>
                      </Entering>
                      <Entering
                        initial={false}
                        amount={0.9}
                        damping={18}
                        stiffness={120}
                        delay={0.2}
                      >
                        <Link href="/book-now" className="flex flex-col items-center">
                          <Button
                            size="x-large"
                            variant="cta"
                            color="white"
                            className="!m-0 !py-6"
                            classNameTypography="!font-medium"
                            label={t('home.block_ten.book_your')}
                          />
                        </Link>
                      </Entering>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PageHome;
